import { Box, Collapsible, ProgressStepper, Tag, TextButton } from '@zillow/constellation-10';
import { IconChevronDownFilled, IconChevronUpFilled } from '@zillow/constellation-icons';
import { LoadingText } from '@zg-rentals/loading-components';

import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import { useWhoHasSignedFromOuidQuery, useWhoHasSignedQuery } from '../../utils/queries';

type Props = {
  isInitiallyExpanded?: boolean;
  leaseId?: number;
  leaseExtensionId?: number;
  usesOuid?: boolean;
};

export default function WhoHasSigned({
  isInitiallyExpanded,
  leaseId,
  leaseExtensionId,
  usesOuid,
}: Props): JSX.Element | null {
  // Get OUID from URL if in OUID context
  const ouid = usesOuid ? new URLSearchParams(window.location.search).get('ouid') || '' : '';

  // Determine artifact type and ID
  const artifactType = leaseExtensionId ? LeaseArtifactType.LEASE_EXTENSION_RENEWAL : LeaseArtifactType.LEASE;
  const artifactId = leaseExtensionId || leaseId || 0;

  // Fetch data - use OUID flow if specified, otherwise use artifact ID
  const whoHasSignedQuery = useWhoHasSignedQuery(artifactId, artifactType, !usesOuid && !!artifactId);
  const whoHasSignedFromOuidQuery = useWhoHasSignedFromOuidQuery(ouid, !!usesOuid && !!ouid);

  const arrayOfSignees = usesOuid ? whoHasSignedFromOuidQuery.data : whoHasSignedQuery.data;
  const isLoading = usesOuid ? whoHasSignedFromOuidQuery.isPending : whoHasSignedQuery.isPending;

  function renderExpandedContent(): JSX.Element | null {
    if (arrayOfSignees == null) {
      return null;
    }

    const isComplete = arrayOfSignees.every((signee) => signee.hasSigned);

    const tenants = arrayOfSignees.filter((signee) => signee.userType === 'tenant');
    const landlords = arrayOfSignees.filter((signee) => signee.userType === 'landlord');

    const allTenantsSigned = tenants.length > 0 && tenants.every((signee) => signee.hasSigned);

    const hasCoLandlord = landlords.length > 1;

    const steps = arrayOfSignees.map((signee) => {
      let tagContent = '';
      let tagTone: 'green' | 'teal' | 'blue' | 'yellow' = 'blue';

      if (signee.userType === 'tenant') {
        if (signee.hasSigned) {
          tagContent = 'Signed';
          tagTone = 'green';
        } else {
          if (signee.isItMe) {
            tagContent = 'Your turn to sign';
            tagTone = 'teal';
          } else {
            tagContent = 'Requested';
            tagTone = 'blue';
          }
        }
      } else if (signee.userType === 'landlord') {
        if (signee.hasSigned) {
          tagContent = 'Signed';
          tagTone = 'green';
        } else {
          const landlordIndex = landlords.findIndex((l) => l.fullName === signee.fullName);
          const isPrimaryLandlord = landlordIndex === 1;
          const isCoLandlord = landlordIndex === 0;

          if (!hasCoLandlord) {
            // Only 1 landlord case
            if (allTenantsSigned) {
              if (signee.isItMe) {
                tagContent = 'Your turn to sign';
                tagTone = 'teal';
              } else {
                tagContent = 'Requested';
                tagTone = 'blue';
              }
            } else {
              tagContent = tenants.length === 1 ? 'Signs after the tenant' : 'Signs after all tenants';
              tagTone = 'yellow';
            }
          } else {
            // 2 landlords case (primary + co-landlord)
            if (isPrimaryLandlord) {
              if (allTenantsSigned && landlords[0].hasSigned) {
                if (signee.isItMe) {
                  tagContent = 'Your turn to sign';
                  tagTone = 'teal';
                } else {
                  tagContent = 'Requested';
                  tagTone = 'blue';
                }
              } else {
                tagContent = 'Last to sign';
                tagTone = 'yellow';
              }
            } else if (isCoLandlord) {
              if (allTenantsSigned) {
                if (signee.isItMe) {
                  tagContent = 'Your turn to sign';
                  tagTone = 'teal';
                } else {
                  tagContent = 'Requested';
                  tagTone = 'blue';
                }
              } else {
                tagContent = tenants.length === 1 ? 'Signs after the tenant' : 'Signs after all tenants';
                tagTone = 'yellow';
              }
            }
          }
        }
      }

      const getDisplayUserType = () => {
        if (signee.isItMe) {
          return 'you';
        }
        if (signee.userType === 'landlord' && hasCoLandlord) {
          const landlordIndex = landlords.findIndex((l) => l.fullName === signee.fullName);
          return landlordIndex === 0 ? 'co-landlord' : 'landlord';
        }
        return signee.userType;
      };

      return {
        label: `${signee.fullName} (${getDisplayUserType()})`,
        status: signee.hasSigned ? 'complete' : 'incomplete',
        meta: (
          <Tag tone={tagTone} size="sm" css={{ textTransform: 'capitalize' }}>
            {tagContent}
          </Tag>
        ),
      };
    });

    return (
      <ProgressStepper activeStepIndex={isComplete ? arrayOfSignees.length : -1} orientation="vertical" steps={steps} />
    );
  }

  function renderLoadingSkeleton() {
    return (
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
          <LoadingText isLoading loadingWidth="12rem">
            Loading
          </LoadingText>
          <LoadingText isLoading loadingWidth="5rem">
            Loading
          </LoadingText>
        </Box>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
          <LoadingText isLoading loadingWidth="11rem">
            Loading
          </LoadingText>
          <LoadingText isLoading loadingWidth="6rem">
            Loading
          </LoadingText>
        </Box>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
          <LoadingText isLoading loadingWidth="13rem">
            Loading
          </LoadingText>
          <LoadingText isLoading loadingWidth="5.5rem">
            Loading
          </LoadingText>
        </Box>
      </Box>
    );
  }

  // Always show the collapsible, but show loading skeleton or actual content
  if (isLoading || arrayOfSignees?.length > 0) {
    return (
      <Collapsible defaultCollapsed={!isInitiallyExpanded} data-test="collapsible">
        {(collapsed, toggle) => (
          <>
            <TextButton
              data-test="expanded-collapsible"
              icon={collapsed ? <IconChevronDownFilled /> : <IconChevronUpFilled />}
              onClick={() => toggle(!collapsed)}
              iconPosition="end"
              tone="brand"
              css={{ marginBottom: collapsed ? 0 : 'tighter' }}
            >
              Who has signed?
            </TextButton>
            {!collapsed && (isLoading ? renderLoadingSkeleton() : renderExpandedContent())}
          </>
        )}
      </Collapsible>
    );
  }

  return null;
}
